import React from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Paperclip, FileImage, FileText as FileTextIcon, ExternalLink } from "lucide-react";

/**
 * Read-only list of attachments already stored on a quote/order.
 *
 * Auth requires a Bearer token, so we cannot use a naked `window.open(url)` —
 * instead we fetch the file as a blob via the axios instance (which attaches
 * the token) and then open the resulting object URL in a new tab. The
 * backend sends the file with `Content-Disposition: inline`, so browsers
 * render PDFs/images directly.
 */
export default function AttachmentList({ endpoint, attachments, compact = false }) {
  if (!attachments || attachments.length === 0) return null;

  const openInNewTab = async (idx, name) => {
    try {
      const res = await api.get(`${endpoint}/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup blocked → fallback: create a temporary link with target=_blank
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Revoke after a short delay so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `'${name}' açılamadı`);
    }
  };

  return (
    <div className={compact ? "space-y-1" : "border-2 border-[#09090B] bg-white"} data-testid="attachment-list">
      {!compact && (
        <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-[#09090B] bg-neutral-50">
          <Paperclip size={14} />
          <span className="font-mono text-xs uppercase tracking-widest">
            Ekler ({attachments.length})
          </span>
        </div>
      )}
      <ul className={compact ? "space-y-1" : "divide-y divide-neutral-200"}>
        {attachments.map((a, idx) => (
          <li
            key={idx}
            className={compact
              ? "flex items-center gap-2 text-xs"
              : "flex items-center gap-3 px-3 py-2"}
            data-testid={`attachment-view-row-${idx}`}
          >
            {a.content_type === "application/pdf" ? (
              <FileTextIcon size={compact ? 12 : 16} className="text-[#FF3B30] shrink-0" />
            ) : (
              <FileImage size={compact ? 12 : 16} className="text-[#0055FF] shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`font-mono ${compact ? "text-xs" : "text-sm"} truncate`}>
                {a.name}
              </div>
              {!compact && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  {a.content_type} · {a.size ? `${(a.size / 1024).toFixed(1)} KB` : "-"}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-2 border-[#09090B] h-8"
              onClick={() => openInNewTab(idx, a.name)}
              data-testid={`attachment-open-${idx}`}
              title="Yeni sekmede aç"
            >
              <ExternalLink size={12} className="mr-1" /> Aç
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
